import React from 'react';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ConfiguratorResultType} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  FlowchartEditorLoadError,
  FlowchartTaskOrSchemaInaccessibleError,
} from 'processes/modals/ProcessFlowchartModalShared';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {useAllSchemasByIdWithSingletonSchemas} from '../useAllSchemasById';
import {useExecutionEditorConfigurator} from './executionFlowchartConfigurator';
import {FlowchartDataResultType, useFlowchartConfigData, useProcessData} from './executionFlowchartData';
import ExecutionFlowchartEditor from './ExecutionFlowchartEditor';
import {PCMExecutionFlowchartPaneProps, PCMProcessData} from './ExecutionFlowchartPaneTypes';

function getPCMFlowchartConfigId(pcmProcessData: PCMProcessData | null | undefined): string | null {
  if (pcmProcessData == null || isInaccessible(pcmProcessData.taskSchemaOrInaccessible)) {
    return null;
  }
  return pcmProcessData.taskSchemaOrInaccessible.flowchartConfig?.id ?? null;
}

function getPCMExecutionFlowchartId(pcmProcessData: PCMProcessData | null | undefined): string | null {
  if (pcmProcessData == null || pcmProcessData.tasks.length === 0) {
    return null;
  }
  return pcmProcessData.tasks[0].executionFlowchart?.id ?? null;
}

export default function PCMExecutionFlowchartPane(props: PCMExecutionFlowchartPaneProps): JSX.Element {
  const pcmProcessData = useProcessData(props.processId);
  const flowchartConfigId = getPCMFlowchartConfigId(pcmProcessData);
  const {configuratorResult, executionEditorConfigurator} = useExecutionEditorConfigurator(props.editorMode);
  const dataResult = useFlowchartConfigData(
    flowchartConfigId,
    executionEditorConfigurator,
    getPCMExecutionFlowchartId(pcmProcessData)
  );
  const data = dataResult.resultType === FlowchartDataResultType.SUCCESS ? dataResult.data : null;

  const needsSidepanel =
    executionEditorConfigurator?.hasFeature(ProcessFlowchartPlatformFeatureType.SHOW_ADD_NODE_SIDEPANEL) ??
    false;

  const {
    schemasById: allSchemasById,
    processTaskDefinitionsResult: taskDefinitionsResult,
    singletonTaskSchemasByName,
  } = useAllSchemasByIdWithSingletonSchemas({
    flowchartEditorConfigurator: executionEditorConfigurator,
    rootTaskSchema: data?.rootTaskSchema ?? null,
    skipTaskDefinitionsQuery: !needsSidepanel,
    availableTaskSchemas: data?.availableTaskSchemas,
  });

  if (pcmProcessData !== undefined && (pcmProcessData == null || flowchartConfigId == null)) {
    return <FlowchartTaskOrSchemaInaccessibleError />;
  }

  if (
    configuratorResult.resultType === ConfiguratorResultType.ERROR ||
    dataResult.resultType === FlowchartDataResultType.ERROR
  ) {
    return <FlowchartEditorLoadError />;
  }

  if (dataResult.resultType === FlowchartDataResultType.INACCESSIBLE) {
    return <FlowchartTaskOrSchemaInaccessibleError />;
  }

  if (
    executionEditorConfigurator == null ||
    data == null ||
    pcmProcessData === undefined ||
    allSchemasById == null
  ) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <ExecutionFlowchartEditor
      editorMode={props.editorMode}
      data={data}
      executionEditorConfigurator={executionEditorConfigurator}
      draftStateRef={props.draftStateRef}
      onBlockingErrorsChange={props.onBlockingErrorsChange}
      allSchemasById={allSchemasById}
      taskDefinitionsResult={taskDefinitionsResult}
      singletonTaskSchemasByName={singletonTaskSchemasByName}
    />
  );
}
